import {
  Component,
  Provide,
  Prop,
  Mixins,
  Watch
} from "vue-property-decorator";
import { mapGetters, mapState } from "vuex";

// Mixins
import Context from "@/mixins/context";
import BrandsStore from "@/mixins/brandsStore";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { FilterOperators, FilterTypes, Sorters } from "@/data/table";
import { TicketStatusCodes, TicketStatusMap } from "@/data/enums/tickets";
import { UUID } from "@/data/enums";
import { UserMetaKeys } from "@/data/enums/users";

// Types
import { IState } from "@/store";
import { IStatus } from "@/models/statuses";
import { IFilter, ISorter, ITableConfig } from "@/models/tables";
import { ITicket, ITicketDepartment } from "@/models/tickets";
import {
  ITicketsProvider,
  ITicketsProviderMethods
} from "@/models/providers/tickets";

@Component({
  beforeRouteEnter(to, from, next) {
    next((vm: any) => {
      vm.isRoutedFromSupport = vm.$_.startsWith(from.path, "/admin/support");
    });
  },
  computed: {
    ...mapState({
      autoRefresh: (state: IState) => state.ui.support.autoRefresh,
      statuses: (state: IState) => state.constants.statuses.ticket,
      storeLimit: (state: IState) => state.ui.support.limit,
      loadAllAtNoResultStore: (state: IState) =>
        state.ui.support.redirectToAllTickets
    }),
    ...mapGetters({
      isMultibrand: "brand/isMultibrand"
    })
  }
})
export default class TicketsProvider extends Mixins(
  Context,
  BrandsStore,
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: "TCKT" })
  public declare id: string;

  // Data
  public autoRefresh!: boolean;
  public dataModule: string = DataModules.TICKETS;
  public isAutoRefreshing = false;
  public isMultibrand!: boolean;
  public isRoutedFromSupport = false;
  public loadAllAtNoResultStore!: boolean;
  public pollInterval: NodeJS.Timer | null = null;
  public statuses!: IStatus[];
  public storeLimit!: number;

  public searchQuery = "";
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public filters: IFilter[] = [
    {
      key: "users.id",
      label: this.$t("_.show"),
      type: FilterTypes.SELECT_RADIO_GROUP,
      operator: FilterOperators.EQUAL,
      options: [
        {
          key: "users.id",
          value: "",
          label: this.$t("_.all_tickets") as string
        },
        {
          key: "lead_user.id",
          value: this.$store.state.user.id,
          label: this.$t("_.my_assigned_tickets") as string
        },
        {
          key: "users.id",
          value: this.$store.state.user.id,
          label: this.$t("_.my_watched_tickets") as string
        },
        {
          key: "lead_user.id",
          value: "null",
          label: this.$t("_.unassigned_tickets") as string
        }
      ],
      value: ""
    },
    {
      key: "created_at",
      label: this.$t("_.opened"),
      type: FilterTypes.SELECT,
      operator: FilterOperators.AFTER_RELATIVE,
      options: [
        {
          value: "",
          label: this.$t("_.anytime") as string
        },
        {
          value: "-24_hours",
          label: this.$t("_.in_the_last_24_hours") as string
        },
        {
          value: "-7_days",
          label: this.$t("_.in_the_last_7_days") as string
        },
        {
          value: "-28_days",
          label: this.$t("_.in_the_last_28_days") as string
        }
      ],
      value: ""
    }
  ];

  public sorters: ISorter[] = [
    {
      field: "updated_at",
      label: this.$t("_.recently_updated"),
      direction: Sorters.ASCENDING
    },
    {
      field: "updated_at",
      label: this.$t("_.recently_updated"),
      direction: Sorters.DESCENDING
    },
    {
      field: "created_at",
      label: this.$t("_.date_opened"),
      direction: Sorters.ASCENDING
    },
    {
      field: "created_at",
      label: this.$t("_.date_opened"),
      direction: Sorters.DESCENDING
    }
  ];

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "updated_at",
      direction: Sorters.DESCENDING
    },
    filters: [
      {
        key: "lead_user.id",
        type: FilterTypes.SELECT_RADIO_GROUP,
        operator: FilterOperators.EQUAL,
        value: `${this.$store.state.user.id}`
      },
      {
        key: "status.code",
        type: FilterTypes.SELECT_CHECKBOX,
        operator: FilterOperators.EQUAL,
        value: [
          TicketStatusCodes.OPEN,
          TicketStatusCodes.IN_PROGESS,
          TicketStatusCodes.CLIENT_REPLIED
        ]
      }
    ],
    constFilters: []
  };

  @Provide()
  public get ticketsProviderMethods(): ITicketsProviderMethods {
    return {
      applyFilters: this.onFiltersChange,
      applySort: this.onSort,
      changeTicketStatus: this.changeTicketStatus,
      debounceSearch: this.debounceSearch,
      doQuickSearch: this.doQuickSearch,
      changeLimit: this.changeLimit,
      onPageChange: this.onPageChange,
      refreshInbox: this.refreshInbox,
      toggleAutoRefresh: this.toggleAutoRefresh,
      updateStoreListItem: this.updateStoreListItem
    };
  }

  @Provide()
  public ticketsProvider(): ITicketsProvider {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      autoRefresh: this.autoRefresh,
      availableFilters: this.filters,
      defaultFilters: this.defaults.filters,
      isLoading: this.table.loading && !this.isAutoRefreshing,
      isAutoRefreshing: this.isAutoRefreshing,
      limit: this.table.limit,
      page: this.table.page,
      searchQuery: this.searchQuery,
      sorters: this.sorters,
      statuses: this.statuses,
      tickets: this.table.data,
      total: this.table.total
    };
  }

  public async initTicketsProvider() {
    this.defaults.limit = this.storeLimit;

    await this.initStatusFilter();
    await this.initDepartmentsFilter();

    this.getData().then(() => {
      if (
        this.loadAllAtNoResultStore &&
        !this.table.total &&
        !this.isRoutedFromSupport
      ) {
        const index = this.activeFilters.findIndex(
          i => i.key === "lead_user.id"
        );
        if (index !== -1) {
          this.activeFilters.splice(index, 1);
          this.onFiltersChange(this.activeFilters);
        }
      }
    });
  }

  public startPolling() {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.pollInterval = setInterval(async () => {
      this.isAutoRefreshing = true;
      await this.refreshInbox();

      this.isAutoRefreshing = false;
    }, 30 * 1000);
  }

  public stopPolling() {
    if (!this.pollInterval) return;
    clearInterval(this.pollInterval);
    this.pollInterval = null;
  }

  public async initStatusFilter() {
    await this.$store.dispatch("constants/getStatuses", "ticket");
    const order = [
      TicketStatusCodes.OPEN,
      TicketStatusCodes.IN_PROGESS,
      TicketStatusCodes.CLIENT_REPLIED,
      TicketStatusCodes.AWAITING_RESPONSE,
      TicketStatusCodes.CLOSED
    ];
    const options = this.$_.sortBy(
      this.$_.map(this.statuses, status => {
        return {
          value: status.code,
          label: status.name,
          ...this.$_.get(TicketStatusMap(), status.code, {})
        };
      }),
      option => order.indexOf(option.value as TicketStatusCodes)
    );
    this.filters.splice(1, 0, {
      key: "status.code",
      label: this.$t("_.with_status"),
      type: FilterTypes.SELECT_CHECKBOX,
      options,
      operator: FilterOperators.EQUAL,
      value: []
    });
  }

  public async initDepartmentsFilter() {
    const departments: ITicketDepartment[] = await this.$store.dispatch(
      "constants/getDepartments",
      {
        ...(this.isMultibrand
          ? {
              params: {
                brand_id: UUID.ORG
              }
            }
          : {}),
        fromCache: !this.isMultibrand && !this.isMockClientContext
      }
    );

    const options = this.$_.map(departments, department => {
      return {
        value: department.id.toString(),
        label: department.name_translated || department.name
      };
    });

    this.filters.splice(2, 0, {
      key: "ticket_department_id",
      label: this.$t("_.in_department"),
      type: FilterTypes.SELECT_CHECKBOX,
      options,
      operator: FilterOperators.EQUAL,
      value: []
    });
  }

  public refreshInbox() {
    return this.getData({
      ignoreStored: true
    });
  }

  public openPreferencesModal() {
    this.$store.dispatch("data/tickets/openPreferencesModal", {
      events: {
        refresh: () => {
          this.$nextTick(() => {
            this.defaults.limit = this.storeLimit;
            this.refreshInbox();
          });
        }
      }
    });
  }

  public updateStoreListItem(ticket: ITicket) {
    const isListed = this.$_.has(
      this.$store.state,
      `data.${this.dataModule}.${this.dataScope}.data.${ticket.id}`
    );
    if (isListed) {
      this.$store.commit(
        `data/updateSingleItemList`,
        {
          scope: this.dataScope,
          path: `data.${this.$_.get(ticket, "id")}`,
          storeModule: this.dataModule,
          data: ticket
        },
        { root: true }
      );
    }
  }

  public changeTicketStatus(statusId: IStatus["id"], ticketId: ITicket["id"]) {
    return this.$store.dispatch(`data/${DataModules.TICKETS}/changeStatus`, {
      id: ticketId,
      statusId
    });
  }

  public async toggleAutoRefresh() {
    return this.$store.dispatch("user/addMeta", {
      [UserMetaKeys.UI_SUPPORT_AUTO_REFRESH]: !this.autoRefresh
    });
  }

  public getStoreParams(payload: any): Promise<any> {
    return {
      ...payload,
      params: {
        ...payload.params,
        with: this.$_.compact([
          this.isMultibrand ? "brand,brand.image" : "",
          "client",
          "client.image",
          "department",
          "lead",
          "lead_user",
          "status",
          "users.image"
        ]).join(),
        ...this.queryFilter
      }
    };
  }

  public async doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    if (!!value && value.length < 3) return Promise.resolve();
    this.cancelRequests();
    return this.getData({
      ignoreStored: true,
      page: 1
    });
  }

  public changeLimit(limit: number) {
    this.$store.dispatch("user/addMeta", {
      [UserMetaKeys.UI_SUPPORT_LIMIT]: limit
    });

    this.onLimitChange(limit);
  }

  public get queryFilter() {
    return this.searchQuery ? { query: this.searchQuery } : {};
  }

  @Watch("autoRefresh", { immediate: true })
  public onAutoRefreshChange() {
    return this.autoRefresh ? this.startPolling() : this.stopPolling();
  }

  @Watch("storeLimit")
  public onLimitChanged() {
    this.onLimitChange(this.storeLimit);
  }

  public beforeDestroy() {
    this.stopPolling(); // Stop auto-refresh (if enabled)
  }
}
